import { compareInventoryLabels, compareInventoryRows, formatInventoryNumber, isInventorySoldOut, matchesInventorySearch } from "../utils/inventoryDisplay";
// src/components/BlockSummaryView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { reservationStatus } from "../utils/stock";
import { roundQuantity } from "../utils/marbleQuantity";

const treatMap = { epoxy: "Epoxy", resin: "Resin", none: "None" };
const faceShort = (x) => (x === "epoxy" ? "E" : x === "resin" ? "R" : "N");
const backShort = (x) => (x === "epoxy" ? "E" : x === "resin" ? "R" : "N");

/* ========= Tooltip صغير جاهز ========= */
function Tooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-block", marginInlineStart: 6 }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span
        title=""
        style={{
          fontSize: 11,
          fontWeight: "bold",
          cursor: "pointer",
          display: "inline-block",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: "var(--accent, #514536)",
          color: "#faf8f4",
          textAlign: "center",
          lineHeight: "16px",
        }}
      >
        ?
      </span>

      {show && (
        <span
          style={{
            position: "absolute",
            bottom: "125%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--accent, #514536)",
            color: "#faf8f4",
            padding: "6px 10px",
            borderRadius: 6,
            whiteSpace: "normal",
            width: "max-content",
            maxWidth: 260,
            fontSize: 12,
            boxShadow: "0 2px 6px rgba(81,69,54,0.2)",
            zIndex: 9999,
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

function buildVariantSuffix(d) {
  const sfx = (d?.variant_suffix || "").trim();
  if (sfx) return sfx; // لو متخزن يدويًا نستخدمه
  const f = faceShort(d?.face_treatment || "none");
  const b = backShort(d?.back_treatment || "none");
  const p = d?.is_polished ? "P" : "";
  const auto = `${f}${b}${p}`;
  return auto || ""; // يرجّع فاضي لو كله none
}

function treatmentTooltip(d) {
  const face = d?.face_treatment || "none";
  const back = d?.back_treatment || "none";
  const polished = !!d?.is_polished;
  return `Front: ${treatMap[face]} • Back: ${treatMap[back]}  ${polished ? "(Polished)" : "(Unpolished)"}`;
}

function BlockSummaryView({ user }) {
  const [blocks, setBlocks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const [materials, setMaterials] = useState([]); // id + name فقط
  const [salesDetails, setSalesDetails] = useState([]);
  const [sales, setSales] = useState([]);
  const [returnsDetails, setReturnsDetails] = useState([]);

  const [showDetails, setShowDetails] = useState(false);
  const [detailsData, setDetailsData] = useState(null);

  const isAdmin = String(user?.role || "").toLowerCase() === "admin";

  // ===== Filters =====
  const [filters, setFilters] = useState({
    q: "",
    material_id: "all",
    showroom_id: "all",
    owner_id: "all",
    availability: "all",
  });

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    const [
      { data: blocksData },
      { data: customersData },
      { data: reservationsData },
      { data: distributionsData },
      { data: showroomsData },
      { data: materialsData },
      { data: salesDetailsData },
      { data: salesData },
      { data: returnsData },
    ] = await Promise.all([
      supabase.from("block").select("*"),
      supabase.from("customer").select("id, name"),
      supabase.from("reservation").select("*"),
      supabase.from("block_distribution").select("*"),
      supabase.from("showroom").select("id, name"),
      supabase.from("material").select("id, name"),
      supabase.from("sale_details").select("*"),
      supabase.from("sale").select("id, customer_id, showroom_id"),
      supabase.from("return_note_details").select("*"),
    ]);

    setBlocks(blocksData || []);
    setCustomers(customersData || []);
    setReservations(reservationsData || []);
    setDistributions(distributionsData || []);
    setShowrooms(showroomsData || []);
    setMaterials(materialsData || []);
    setSalesDetails(salesDetailsData || []);
    setSales(salesData || []);
    setReturnsDetails(returnsData || []);
  }

  // ===== Lookups =====
  const customerLookup = useMemo(
    () => Object.fromEntries((customers || []).map((c) => [c.id, c.name])),
    [customers]
  );
  const showroomLookup = useMemo(
    () => Object.fromEntries((showrooms || []).map((s) => [s.id, s.name])),
    [showrooms]
  );
  const materialLookup = useMemo(
    () => Object.fromEntries((materials || []).map((m) => [String(m.id), m.name])),
    [materials]
  );
  const saleLookup = useMemo(
    () => Object.fromEntries((sales || []).map((s) => [s.id, s])),
    [sales]
  );

  // ===== Options =====
  const materialOptions = useMemo(() => {
    return [
      { id: "all", name: "All materials" },
      ...(materials || []).map((m) => ({
        id: String(m.id),
        name: m.name || `Material #${m.id}`,
      })).sort((a, b) => compareInventoryLabels(a.name, b.name)),
    ];
  }, [materials]);

  const showroomOptions = useMemo(
    () => [{ id: "all", name: "All showrooms" }, ...(showrooms || []).map((s) => ({ id: s.id, name: s.name }))],
    [showrooms]
  );

  const ownerOptions = useMemo(
    () => [{ id: "all", name: "All" }, ...Object.entries(customerLookup).map(([id, name]) => ({ id: Number(id), name }))],
    [customerLookup]
  );

  // ===== Returns per detail =====
  const returnsMap = useMemo(() => {
    const m = {};
    (returnsDetails || []).forEach((r) => {
      const k = r.sale_detail_id;
      if (!m[k]) m[k] = { slabs: 0, area: 0 };
      m[k].slabs = roundQuantity(m[k].slabs + Number(r.slabs_returned || 0));
      m[k].area += Number(r.area_returned_m2 || 0);
    });
    return m;
  }, [returnsDetails]);

  function getReturnForDetail(detailId) {
    return returnsMap[detailId] || { slabs: 0, area: 0 };
  }

  // ===== Quick calcs =====
  const soldNetByBlock = useMemo(() => {
    const map = {};
    for (const s of salesDetails) {
      const blockId = s.block_id;
      const sold = Number(s.slabs_sold || 0);
      const ret = Number((returnsMap[s.id]?.slabs) || 0);
      const net = Math.max(0, sold - ret);
      map[blockId] = (map[blockId] || 0) + net;
    }
    return map;
  }, [salesDetails, returnsMap]);

  const distCurrentByBlock = useMemo(() => {
    const map = {};
    for (const d of distributions) {
      map[d.block_id] = roundQuantity((map[d.block_id] || 0) + Number(d.slabs_count || 0));
    }
    return map;
  }, [distributions]);

  const reservedByBlock = useMemo(() => {
    const distsById = Object.fromEntries((distributions || []).map((d) => [d.id, d]));
    const map = {};
    for (const r of reservations) {
      const dist = distsById[r.distribution_id];
      if (!dist || reservationStatus(r) !== "active") continue;
      const blockId = dist.block_id;
      map[blockId] = roundQuantity((map[blockId] || 0) + Number(r.slabs_reserved || 0));
    }
    return map;
  }, [reservations, distributions]);

  function getSoldSlabsNet(block) {
    return Number(soldNetByBlock[block.id] || 0);
  }
  function getDistributedCurrent(block_id) {
    return Number(distCurrentByBlock[block_id] || 0);
  }
  function getReservedTotal(block_id) {
    return Number(reservedByBlock[block_id] || 0);
  }
  function getOriginalSlabs(block) {
    return getDistributedCurrent(block.id) + getSoldSlabsNet(block);
  }
  function getAvailableSlabs(block) {
    return Math.max(0, getDistributedCurrent(block.id) - getReservedTotal(block.id));
  }

  // ===== Helpers =====
  function hasDistInShowroom(blockId, showroomId) {
    return (distributions || []).some(
      (d) => d.block_id === blockId && Number(d.showroom_id) === Number(showroomId)
    );
  }

  // ===== Filtering =====
  const filteredBlocks = useMemo(() => {
    return (blocks || []).filter((block) => {
      const hasDistribution = (distributions || []).some((d) => d.block_id === block.id);
      const hasBaseCount = Number(block.slabs_number || 0) > 0;
      if (!hasDistribution && !hasBaseCount) return false;

      if (!matchesInventorySearch({ code: block.code, materialName: materialLookup[String(block.material_id)] }, filters.q)) return false;

      if (
        filters.material_id !== "all" &&
        String(block.material_id) !== String(filters.material_id)
      ) {
        return false;
      }

      if (filters.showroom_id !== "all" && !hasDistInShowroom(block.id, filters.showroom_id)) {
        return false;
      }

      if (filters.owner_id !== "all" && Number(block.customer_id) !== Number(filters.owner_id)) {
        return false;
      }

      const available = getAvailableSlabs(block);
      const reserved = getReservedTotal(block.id);
      const distNow = getDistributedCurrent(block.id);

      if (filters.availability === "available" && available <= 0) return false;
      if (filters.availability === "reservedOnly" && reserved <= 0) return false;
      if (filters.availability === "out" && distNow > 0) return false;

      return true;
    }).sort((a, b) => compareInventoryRows(
      { code: a.code, blockId: a.id, materialName: materialLookup[String(a.material_id)] },
      { code: b.code, blockId: b.id, materialName: materialLookup[String(b.material_id)] }
    ));
  }, [blocks, distributions, filters, reservedByBlock, distCurrentByBlock, materialLookup]);

  // ===== Reserved details list =====
  function reservedDetails(block_id) {
    const blockDists = (distributions || []).filter((d) => d.block_id === block_id);
    const blockReservations = (reservations || []).filter((r) =>
      reservationStatus(r) === "active" && blockDists.some((d) => String(d.id) === String(r.distribution_id))
    );
    if (!blockReservations.length) return "-";
    return (
      <ul style={{ paddingRight: 18, margin: 0, fontSize: 14 }}>
        {blockReservations.map((r) => {
          const dist = (distributions || []).find((d) => d.id === r.distribution_id);
          const showroomName = dist ? showroomLookup[dist.showroom_id] || dist.showroom_id : "-";
          const customerName = customerLookup[r.customer_id] || r.customer_id;
          return (
            <li key={r.id} style={{ marginBottom: 3 }}>
              <b>{customerName}</b>: {formatInventoryNumber(r.slabs_reserved)} Table
              <span style={{ color: "#716759" }}> ({showroomName})</span>
              {r.notes && <> <span style={{ color: "#716759" }}>— {r.notes}</span></>}
            </li>
          );
        })}
      </ul>
    );
  }

  // ===== توزيع البلوكات بالمعارض (suffix + Tooltip) =====
  function distributionGrouped(block) {
    const dists = (distributions || []).filter((d) => d.block_id === block.id);
    if (!dists.length) return "-";

    const byShowroom = {};
    for (const d of dists) {
      const sid = d.showroom_id;
      if (!byShowroom[sid]) byShowroom[sid] = [];
      byShowroom[sid].push(d);
    }

    return (
      <div className="" style={{ display: "grid", gap: 6 }}>
        {Object.entries(byShowroom).map(([sid, list]) => {
          const sname = showroomLookup[Number(sid)] || `Showroom ${sid}`;
          return (
            <div key={sid} style={{ marginBottom: 4 }}>
              <div
                style={{
                  fontWeight: "bold",
                  color: "rgba(81,69,54, 1)",
                  marginBottom: 3,
                  borderBottom: "1px dashed #514536",
                  backgroundColor: "#d2cbc1",
                }}
              >
                {sname}
              </div>
              <ul style={{ margin: 0, paddingRight: 18 }}>
                {list.map((d) => {
                  const low = Number(d.slabs_count || 0) <= 5;
                  const color = low ? "#5145369c" : "#514536";
                  const suffix = buildVariantSuffix(d);
                  const tooltipTxt = treatmentTooltip(d);

                  return (
                    <li key={d.id} style={{ color, fontWeight: "bold" }}>
                      {suffix || "-"} : {formatInventoryNumber(Number(d.slabs_count || 0))} Table
                      <Tooltip text={tooltipTxt} />
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  // ===== تفاصيل المبيعات (بعد المرتجع) =====
  function handleShowDetails(block) {
    const salesForBlock = (salesDetails || []).filter((s) => s.block_id === block.id);

    const grouped = {};
    let grandTotalNet = 0;
    let totalReturnedForBlock = 0;

    salesForBlock.forEach((s) => {
      const sale = saleLookup[s.sale_id];
      const showroomName = sale ? showroomLookup[sale.showroom_id] || sale.showroom_id : "-";
      const customerName = sale ? customerLookup[sale.customer_id] || sale.customer_id : "-";
      const key = `${showroomName}__${customerName}`;

      const ret = getReturnForDetail(s.id);
      totalReturnedForBlock += Number(ret.slabs || 0);

      const slabs_sold = Number(s.slabs_sold || 0);
      const area_sold = Number(s.area_sold || 0);
      const price = Number(s.price_per_m2 || 0);

      const net_slabs = Math.max(0, roundQuantity(slabs_sold - Number(ret.slabs || 0)));
      const net_area = Math.max(0, area_sold - Number(ret.area || 0));
      const net_total = Number((net_area * price).toFixed(2));

      if (net_slabs <= 0 || net_area <= 0) return;

      if (!grouped[key]) {
        grouped[key] = { showroom: showroomName, customer: customerName, list: [] };
      }
      grouped[key].list.push({
        slabs_net: net_slabs,
        area_net: net_area.toFixed(2),
        price,
        total_net: net_total.toFixed(2),
      });

      grandTotalNet += net_total;
    });

    setDetailsData({ block, grouped, returned: totalReturnedForBlock, grandTotalNet });
    setShowDetails(true);
  }

  // ===== Styles =====
  const thStyle = {
    padding: "6px 8px",
    borderBottom: "1.5px solid #d2cbc1",
    background: "#f2ede4",
    fontWeight: "bold",
    fontSize: 13,
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "6px 8px",
    fontSize: 13,
    textAlign: "left",
    verticalAlign: "top",
    background: "#faf8f4",
  };

  // Totals للنتائج المفلترة
  const totalsFiltered = useMemo(() => {
    let totalAvailable = 0;
    let totalReserved = 0;
    let totalSoldNet = 0;
    (filteredBlocks || []).forEach((b) => {
      totalAvailable += getAvailableSlabs(b);
      totalReserved += getReservedTotal(b.id);
      totalSoldNet += getSoldSlabsNet(b);
    });
    return { totalAvailable, totalReserved, totalSoldNet };
  }, [filteredBlocks, reservedByBlock, distCurrentByBlock, soldNetByBlock]);

  return (
    <div className="ui-legacy-page"
      dir="ltr"
      style={{
        textAlign: "left",
        fontFamily: "Jost, 'Trebuchet MS', Arial, sans-serif",
        background: "#f2ede4",
        minHeight: "100vh",
      }}
    >
      <h2
        style={{
          color: "var(--accent, #514536)",
          fontWeight: "bold",
          margin: "30px 0 26px 0",
          textAlign: "center",
        }}
      >
        Block summary
      </h2>

      {/* ===== Filters Bar ===== */}
      <div className="ui-card"
        style={{
          background: "#faf8f4",
          padding: "10px 12px",
          borderRadius: 12,
          boxShadow: "0 1px 10px #5145360a",
          margin: "0 auto 14px",
          maxWidth: 900,
          display: "grid",
          gap: 8,
        }}
      >
        <div className="ui-toolbar" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <label className="ui-field">Search material or block code<input
            type="search"
            aria-label="Search material or block code"
            placeholder="Search material or block code"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            style={{ flex: "1 1 160px", minWidth: 160, padding: "6px 8px" }}
          /></label>

          <label className="ui-field">Material<select
            value={filters.material_id}
            onChange={(e) => setFilters((f) => ({ ...f, material_id: e.target.value }))}
            style={{ minWidth: 180, padding: "6px 8px" }}
            title="Material"
          >
            {materialOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select></label>

          <label className="ui-field">Showroom<select
            value={filters.showroom_id}
            onChange={(e) => setFilters((f) => ({ ...f, showroom_id: e.target.value }))}
            style={{ minWidth: 160, padding: "6px 8px" }}
          >
            {showroomOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select></label>

          <label className="ui-field">Supplier<select
            value={filters.owner_id}
            onChange={(e) => setFilters((f) => ({ ...f, owner_id: e.target.value }))}
            style={{ minWidth: 140, padding: "6px 8px" }}
            title="Block supplier"
          >
            {ownerOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select></label>

          <label className="ui-field">Stock status<select
            value={filters.availability}
            onChange={(e) => setFilters((f) => ({ ...f, availability: e.target.value }))}
            style={{ minWidth: 140, padding: "6px 8px" }}
            title="Stock status"
          >
            <option value="all">All statuses</option>
            <option value="available">Available only</option>
            <option value="reservedOnly">Reserved only</option>
            <option value="out">Sold out (0 Distributed)</option>
          </select></label>

          <button className="ui-secondary"
            onClick={() =>
              setFilters({
                q: "",
                material_id: "all",
                showroom_id: "all",
                owner_id: "all",
                availability: "all",
              })
            }
            style={{
              marginRight: "auto",
              background: "#f2ede4",
              border: "1px solid #d2cbc1",
              padding: "6px 12px",
              borderRadius: 8,
              color: "var(--accent, #514536)",
              fontWeight: "bold",
            }}
            title="Reset filters to their defaults"
          >
            Reset
          </button>

          <div style={{ marginRight: "auto", color: "var(--accent, #514536)", fontWeight: "bold" }}>
            Results: {filteredBlocks.length.toLocaleString()}
          </div>
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", color: "var(--accent, #514536)", fontWeight: "bold" }}>
          <div>Total available: {totalsFiltered.totalAvailable.toLocaleString()} Table</div>
          <div>Total reserved: {totalsFiltered.totalReserved.toLocaleString()} Table</div>
          <div>Total sold (Net): {totalsFiltered.totalSoldNet.toLocaleString()} Table</div>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <div className="ui-table-scroll"><table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            maxWidth: 900,
            background: "#faf8f4",
            borderRadius: 12,
            boxShadow: "0 1px 10px #51453610",
            direction: "ltr",
            fontSize: 12,
          }}
        >
          <thead>
            <tr style={{ background: "#f2ede4", color: "var(--accent, #514536)" }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Code</th>
              <th style={thStyle}>Material</th>
              <th style={{ ...thStyle, whiteSpace: "normal", lineHeight: 1.2 }}>
                Count
                <br />
                Original slabs
              </th>
              <th style={thStyle}>Sold (Net)</th>
              <th style={thStyle}>Reserved</th>
              <th style={{ ...thStyle, whiteSpace: "normal" }}>Available</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Showroom block distributions</th>
              <th style={{ ...thStyle, whiteSpace: "normal", lineHeight: 1.2 }}>
                Supplier
                <br />
                Block
              </th>
              <th style={thStyle}>Notes</th>
            </tr>
          </thead>

          <tbody>
            {filteredBlocks.map((block, i) => (
              <tr key={block.id} style={{ borderBottom: "1px solid var(--line, #d2cbc1)" }}>
                <td style={tdStyle}>{i + 1}</td>
                <td style={tdStyle}>{block.code}</td>

                <td style={tdStyle}>
                  {materialLookup[String(block.material_id)] || String(block.material_id)}
                </td>

                <td style={tdStyle}>{formatInventoryNumber(getOriginalSlabs(block))}</td>

                <td style={tdStyle}>
                  {formatInventoryNumber(getSoldSlabsNet(block))}
                  {isAdmin && getSoldSlabsNet(block) > 0 && (
                    <button className="ui-secondary"
                      style={{
                        fontSize: 10,
                        marginRight: 6,
                        padding: "1px 8px",
                        borderRadius: 6,
                        background: "#f2ede4",
                        border: "1px solid #d2cbc1",
                        cursor: "pointer",
                        color: "#514536",
                        fontWeight: "bold",
                      }}
                      onClick={() => handleShowDetails(block)}
                      title="Sales details (after returns)"
                    >
                      Details
                    </button>
                  )}
                </td>

                <td style={tdStyle}>{reservedDetails(block.id)}</td>

                <td
                  style={{
                    ...tdStyle,
                    color:
                      getAvailableSlabs(block) === 0
                        ? "#514536"
                        : getAvailableSlabs(block) < 5
                        ? "#514536"
                        : "var(--accent, #514536)",
                    fontWeight: "bold",
                  }}
                >
                  {isInventorySoldOut({ slabs: distCurrentByBlock[block.id] ?? block.slabs_number }) ? "Sold out" : formatInventoryNumber(getAvailableSlabs(block))}
                </td>

                <td style={tdStyle}>{distributionGrouped(block)}</td>

                <td style={tdStyle}>{customerLookup[block.customer_id] || "-"}</td>

                <td className="ui-table-notes" style={tdStyle}>{block.notes_text || block.notes || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {/* مودال التفاصيل */}
      {showDetails && (
        <div className="ui-backdrop"
          style={{
            position: "fixed",
            right: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            background: "#51453677",
            zIndex: 99,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowDetails(false)}
        >
          <div className="ui-modal ui-modal-small"
            style={{
              background: "#faf8f4",
              borderRadius: 14,
              padding: 24,
              minWidth: 340,
              minHeight: 180,
              boxShadow: "0 2px 32px #51453633",
              maxWidth: "96vw",
              direction: "ltr",
              textAlign: "left",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              Block sales details{" "}
              <span style={{ color: "var(--accent, #514536)" }}>{detailsData?.block.code}</span>
            </h3>

            {detailsData && Number(detailsData.returned || 0) > 0 && (
              <div style={{ margin: "4px 0 10px", color: "#514536" }}>
                Returns have been deducted — Total returns:{" "}
                <b>{formatInventoryNumber(detailsData.returned)}</b> slabs
              </div>
            )}

            {detailsData && Object.keys(detailsData.grouped).length === 0 && (
              <div>No sales for this block.</div>
            )}

            {detailsData &&
              Object.values(detailsData.grouped).map((g, idx) => (
                <div key={idx} style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: "bold", color: "#514536" }}>
                    {g.showroom} — {g.customer}
                  </div>
                  <ul style={{ margin: "6px 0 0 12px", padding: 0 }}>
                    {g.list.map((c, subIdx) => (
                      <li key={subIdx} style={{ fontSize: 15, lineHeight: 1.6 }}>
                        <b>{formatInventoryNumber(c.slabs_net)}</b> slabs — <b>{formatInventoryNumber(c.area_net)}</b> m² @ {formatInventoryNumber(c.price)} per meter
                        <span style={{ color: "#716759", marginRight: 6 }}>
                          [Total: <b>{c.total_net}</b>]
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

            {detailsData && (
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 6,
                  borderTop: "1px dashed #d2cbc1",
                  color: "var(--accent, #514536)",
                  fontWeight: "bold",
                }}
              >
                Block total (after returns):{" "}
                {Number(detailsData.grandTotalNet || 0).toLocaleString()} EGP
              </div>
            )}

            <div style={{ textAlign: "left" }}>
              <button className="ui-primary"
                onClick={() => setShowDetails(false)}
                style={{
                  marginTop: 14,
                  background: "var(--accent, #514536)",
                  color: "#faf8f4",
                  border: "none",
                  borderRadius: 6,
                  padding: "7px 22px",
                  fontWeight: "bold",
                  fontSize: 15,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlockSummaryView;
